import { useState } from 'react';
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../store/admin.slice';
import { logout as logoutRequest } from '../api/auth';
import { ADMIN_STORAGE_KEY } from '../api/client';
import { useAdminData } from '../hooks/useAdminData';
import { brand } from '../app/config';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const NAV_ITEMS = [
    { to: '/admin/events', label: 'Eventi' },
    { to: '/admin/requests', label: 'Richieste' },
    { to: '/admin/types', label: 'Tipi' },
    { to: '/admin/files', label: 'File' },
];

const DRAWER_WIDTH = 220;

// Stile comune della carta della sidebar. Il posizionamento dipende dal variant:
// MUI applica `position: fixed` alla carta anche per `permanent`, quindi su desktop
// va sovrascritto perché la sidebar occupi spazio nel layout flex.
const PAPER_BASE = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    borderRight: '1px solid rgba(255,255,255,0.08)',
} as const;

export function AdminLayout() {
    useDocumentTitle('Amministrazione');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const admin = useAdminData();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);

    // A4: la sessione admin viene ripristinata dallo store (`preloadedState` in
    // `store/index.ts`), quindi qui non serve più leggerla da localStorage.
    const handleLogout = () => {
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        dispatch(logout());
        // M4: azzera anche il cookie di autenticazione lato server.
        void logoutRequest().catch(() => undefined);
        navigate('/admin/login');
    };

    const closeMobileMenu = () => setMobileOpen(false);

    const sidebarContent = (
        <>
            <Toolbar>
                <Typography variant="h6" sx={{ fontWeight: 900 }} color="primary">
                    {brand.name}
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {NAV_ITEMS.map((item) => (
                    <ListItemButton
                        key={item.to}
                        component={NavLink}
                        to={item.to}
                        onClick={closeMobileMenu}
                        sx={(theme) => ({
                            '&.active': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                color: theme.palette.primary.light,
                            },
                        })}
                    >
                        <ListItemText primary={item.label} />
                    </ListItemButton>
                ))}
            </List>
            <Box sx={{ flexGrow: 1 }} />
            <List>
                <ListItemButton onClick={handleLogout}>
                    <ListItemText primary="Esci" />
                </ListItemButton>
            </List>
        </>
    );

    return (
        <Box className="flex min-h-screen">
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={mobileOpen}
                    onClose={closeMobileMenu}
                    ModalProps={{ keepMounted: true }}
                    sx={{ '& .MuiDrawer-paper': { ...PAPER_BASE, display: 'flex', flexDirection: 'column' } }}
                >
                    {sidebarContent}
                </Drawer>
            ) : (
                <Drawer
                    variant="permanent"
                    anchor="left"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            ...PAPER_BASE,
                            position: 'sticky',
                            top: 0,
                            height: '100vh',
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}
                >
                    {sidebarContent}
                </Drawer>
            )}

            <Box component="main" className="min-w-0 flex-1 p-6">
                {isMobile && (
                    <Box className="mb-4 flex items-center gap-2">
                        <IconButton aria-label="Apri il menu" onClick={() => setMobileOpen(true)} color="primary">
                            <MenuIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                        <Typography variant="h6" sx={{ fontWeight: 900 }} color="primary">
                            {brand.name}
                        </Typography>
                    </Box>
                )}
                {admin.host_name && (
                    <Typography variant="subtitle2" color="text.secondary" className="mb-4">
                        Autenticato come: {admin.host_name}
                    </Typography>
                )}
                <Outlet />
            </Box>
        </Box>
    );
}
